// ── RiskAcceptanceActionModal — shared action modal for vendor RA + ORA clusters ─
//
// ORA.3b: extracted from RaActionModal (vendor-RA only) into shared/ so both
// vendor RA pages and ORA pages can use the same UI component.
//
// The mutation hook is injected via the `mutationHook` prop, allowing each domain
// to supply its own hook (useRiskAcceptanceAction vs useOrgRiskAcceptanceAction)
// without coupling the shared modal to either domain's service layer.
//
// Pattern constraints:
//   - Native useState + canSubmit boolean (cluster has no React Hook Form)
//   - No Zod client-side; server enforces full schema; inline guard only
//   - ACTION_CONFIG has no Zod schemas (cluster-canonical per D-RA.3b-PE-4)
//   - fieldErrors object envelope: cluster's first consumer (D-RA.3b-PE-4)
//   - 4-eyes UI: caller's Approve button pre-disables; modal shows in-modal error for 422
//
// Caller pattern:
//   vendor RA: <RiskAcceptanceActionModal mutationHook={useRiskAcceptanceAction} ... />
//   ORA:       <RiskAcceptanceActionModal mutationHook={useOrgRiskAcceptanceAction} ... />

import { useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import type { RaActionType } from '@/api/hooks/useRiskAcceptances';

// ── Action configuration (shared — identical for vendor RA and ORA clusters) ───

const ACTION_CONFIG = {
  approve: {
    title: 'Approve Risk Response',
    fieldLabel: 'Approval notes',
    fieldName: 'approval_notes',
    fieldMinChars: 50,
    fieldMaxChars: 2000,
    submitLabel: 'Approve',
  },
  deny: {
    title: 'Deny Risk Response',
    fieldLabel: 'Denial reason',
    fieldName: 'denial_reason',
    fieldMinChars: 50,
    fieldMaxChars: 2000,
    submitLabel: 'Deny',
  },
  return: {
    title: 'Return for Information',
    fieldLabel: 'Comments to Submitter',
    fieldName: 'return_comments',
    fieldMinChars: 30,
    fieldMaxChars: 2000,
    submitLabel: 'Return',
  },
  'close-as-mitigated': {
    title: 'Close as Mitigated',
    fieldLabel: 'Mitigation summary',
    fieldName: 'mitigation_summary',
    fieldMinChars: 50,
    fieldMaxChars: 2000,
    submitLabel: 'Close',
  },
  revoke: {
    title: 'Revoke Approval',
    fieldLabel: 'Revocation reason',
    fieldName: 'revocation_reason',
    fieldMinChars: 50,
    fieldMaxChars: 2000,
    submitLabel: 'Revoke',
  },
  // RA.5 / ORA.5: renewal approval/denial
  'approve-renewal': {
    title: 'Approve Renewal',
    fieldLabel: 'Approval notes',
    fieldName: 'approval_notes',
    fieldMinChars: 50,
    fieldMaxChars: 2000,
    submitLabel: 'Approve Renewal',
  },
  'deny-renewal': {
    title: 'Deny Renewal',
    fieldLabel: 'Denial reason',
    fieldName: 'denial_reason',
    fieldMinChars: 50,
    fieldMaxChars: 2000,
    submitLabel: 'Deny Renewal',
  },
} as const;

type RaAction = RaActionType;

// ── Props ─────────────────────────────────────────────────────────────────────

// mutationHook: factory function — accepts action string, returns mutation object.
// Both useRiskAcceptanceAction and useOrgRiskAcceptanceAction satisfy this shape.
// The action parameter is typed as `string` here to avoid TypeScript contravariance
// errors when callers pass domain-specific hooks (RaActionType vs OraActionType).
// Type safety is preserved at the call-site where callers pass a typed action.
export interface RiskAcceptanceActionModalProps {
  action: RaAction;
  entry: { id: string; title: string | null };
  onSuccess: (updatedEntry: unknown) => void;
  onClose: () => void;
  mutationHook: (action: string) => {
    mutateAsync: (args: { id: string; payload: Record<string, string> }) => Promise<unknown>;
    isPending: boolean;
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

export function RiskAcceptanceActionModal({
  action,
  entry,
  onSuccess,
  onClose,
  mutationHook,
}: RiskAcceptanceActionModalProps) {
  const config = ACTION_CONFIG[action];
  const [value, setValue] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [inModalError, setInModalError] = useState('');

  const actionMutation = mutationHook(action);

  // Inline guard: canSubmit boolean per §3.7 + §10.3
  const trimmed = value.trim();
  const canSubmit =
    trimmed.length >= config.fieldMinChars &&
    trimmed.length <= config.fieldMaxChars;

  async function handleSubmit() {
    setFieldError('');
    setInModalError('');

    const payload = { [config.fieldName]: trimmed } as Record<string, string>;

    try {
      const updatedEntry = await actionMutation.mutateAsync({ id: entry.id, payload });
      onSuccess(updatedEntry);
      onClose();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { errors?: unknown } } };
      const status = axiosErr?.response?.status;
      const errData = axiosErr?.response?.data?.errors;

      if (status === 400) {
        // §6.3: detect envelope shape — object (fieldErrors) vs array (typed)
        if (errData && !Array.isArray(errData) && typeof errData === 'object') {
          const fe = errData as Record<string, string[]>;
          const msg = fe[config.fieldName]?.[0] ?? Object.values(fe)[0]?.[0] ?? 'Validation error';
          setFieldError(msg);
        } else if (Array.isArray(errData)) {
          toast.error((errData as Array<{ message: string }>)[0]?.message ?? 'Validation error');
        } else {
          toast.error('Validation error');
        }
      } else if (status === 422) {
        // FourEyesViolation — in-modal error display (rare; caller pre-disables Approve)
        setInModalError('4-eyes rule: you cannot perform this action on a submission you created.');
      } else if (status === 409) {
        toast.error("This entry's state changed. Please refresh to see the latest.");
        onClose();
      } else if (status === 403) {
        toast.error("You don't have permission for this action");
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    }
  }

  const charCount = trimmed.length;
  const charHint =
    charCount < config.fieldMinChars
      ? `at least ${config.fieldMinChars} characters (${charCount}/${config.fieldMinChars})`
      : `${charCount}/${config.fieldMaxChars}`;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{config.title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="text-sm text-text-secondary">
            <span className="font-medium text-text-primary">{entry.title}</span>
          </div>

          {inModalError && (
            <div className="rounded-md bg-accent-rose-dim px-3 py-2 text-sm text-accent-rose">
              {inModalError}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="ra-action-field">{config.fieldLabel}</Label>
            <Textarea
              id="ra-action-field"
              value={value}
              onChange={(e) => { setValue(e.target.value); setFieldError(''); }}
              rows={5}
              maxLength={config.fieldMaxChars}
              placeholder={`${config.fieldMinChars}–${config.fieldMaxChars} characters`}
              className="resize-none"
            />
            {fieldError ? (
              <p className="text-xs text-accent-rose">{fieldError}</p>
            ) : (
              <p className="text-xs text-text-secondary">{charHint}</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={actionMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || actionMutation.isPending}
            variant={
              action === 'approve' || action === 'close-as-mitigated' || action === 'approve-renewal' ? 'default' :
              action === 'deny' || action === 'revoke' || action === 'deny-renewal' ? 'destructive' :
              'outline'
            }
          >
            {actionMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {config.submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
